import { Box, Button, Container, FloatingIndicator, Group, Scroller } from "@mantine/core";
import { useMemo, useState, type FC } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";

import Icon from "src/components/common/Icon";
import type { ILayoutLink } from "src/contexts/layouts/LayoutContext";
import useCurrentUserPermissions from "src/hooks/authentication/useCurrentUserPermissions";
import { filterLayoutLinksByPermission } from "src/utilities/hasLayoutLinkPermission";

import styles from "./NavMenu.module.scss";

/**
 * Defines the inputs for this navigation presentation.
 */
interface INavMenuProps {
    menuItems?: ILayoutLink[];
}

/**
 * Defines the inputs for this navigation presentation.
 */
interface INavMenuInternalProps {
    menuItems: ILayoutLink[];
}

/**
 * Renders permission-filtered top-level navigation and its active indicator.
 *
 * Postconditions: The first allowed href that prefixes the pathname determines the indicator.
 * Action items invoke their callback; link items preserve router navigation and new-tab behavior.
 */
const NavMenuInternal: FC<INavMenuInternalProps> = ({ menuItems }) => {
    const permissions = useCurrentUserPermissions();
    const location = useLocation();

    const [rootRef, setRootRef] = useState<HTMLDivElement | null>(null);
    const [controlsRefs, setControlsRefs] = useState<Record<string, HTMLAnchorElement | null>>({});

    const navigation = useMemo(
        () => {
            /**
             * Creates a callback that requests an updated href-to-element map for the active indicator.
             */
            const setControlRef = (href: string) => (node: HTMLAnchorElement) => {
                setControlsRefs(current => {
                    const newRefs = { ...current };
                    newRefs[href] = node;
                    return newRefs;
                });
            };

            const allowedMenuItems = filterLayoutLinksByPermission(permissions, menuItems);

            const items: React.ReactNode[] = [];

            for (const menuItem of allowedMenuItems) {
                if (menuItem.actionFn) {
                    items.push(
                        <Button
                            key={menuItem.href}
                            radius={0}
                            size="md"
                            ref={setControlRef(menuItem.href) as any}
                            onClick={menuItem.actionFn}
                            leftSection={
                                menuItem.label && menuItem.icon ? (
                                    <Icon icon={menuItem.icon} />
                                ) : undefined
                            }
                            style={menuItem.style}
                            className={menuItem.className}
                        >
                            {menuItem.label ? (
                                menuItem.label
                            ) : menuItem.icon ? (
                                <Icon icon={menuItem.icon} />
                            ) : null}
                        </Button>
                    );

                    continue;
                }

                items.push(
                    <Button
                        key={menuItem.href}
                        radius={0}
                        size="md"
                        ref={setControlRef(menuItem.href)}
                        component={RouterLink}
                        to={menuItem.href}
                        target={menuItem.newTab ? "_blank" : undefined}
                        leftSection={
                            menuItem.label && menuItem.icon ? <Icon icon={menuItem.icon} /> : undefined
                        }
                        style={menuItem.style}
                        className={menuItem.className}
                    >
                        {menuItem.label ? (
                            menuItem.label
                        ) : menuItem.icon ? (
                            <Icon icon={menuItem.icon} />
                        ) : null}
                    </Button>
                );
            }

            return {
                items,
                allowedMenuItems
            };
        },
        [menuItems, permissions, setControlsRefs]
    );

    const selectedHref = useMemo(
        () => {
            return navigation.allowedMenuItems.find(menuItem =>
                location.pathname.startsWith(menuItem.href)
            )?.href;
        },
        [navigation, location.pathname]
    );

    return (
        <Box bg="primary">
            <Container size="xl">
                <Scroller
                    draggable
                    edgeGradientColor="primary"
                    styles={{
                        content: {
                            minWidth: "100%"
                        }
                    }}
                >
                    <Group
                        gap={0}
                        ref={setRootRef}
                        pos="relative"
                        wrap="nowrap"
                        w="100%"
                    >
                        {navigation.items}
                        <FloatingIndicator
                            target={controlsRefs[selectedHref]}
                            parent={rootRef}
                            className={styles.indicator}
                        />
                    </Group>
                </Scroller>
            </Container>
        </Box>
    );
};

/**
 * Renders no navigation for an absent or empty input collection.
 */
const NavMenu: FC<INavMenuProps> = ({ menuItems }) => {
    if (!menuItems || menuItems.length === 0) {
        return null;
    }

    return <NavMenuInternal menuItems={menuItems} />;
};

export default NavMenu;
